import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header';

const getCategoryIds = (course) => {
    return Array.isArray(course.categories)
        ? course.categories.map(cat => typeof cat === 'object' ? cat.id : cat)
        : [];
};

export const ManagePopular = () => {
    const [courses, setCourses] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [mostPopularCategory, setMostPopularCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [addedIds, setAddedIds] = useState([]);
    const [removedIds, setRemovedIds] = useState([]);
    const [publishError, setPublishError] = useState(null);
    const { user } = useContext(UserContext);

    const snapshotRef = useRef([]);

    const headers = {
        'Authorization': `Bearer ${user.token}`,
        'X-Editor': 'nanoms',
        'Content-Type': 'application/json'
    };

    const getCategories = useCallback(async () => {
        const res = await axios.get(`/api/courses/categories?per_page=200`, {
            headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' }
        });
        return res.data.items;
    }, [user.token]);

    const getCourses = useCallback(async () => {
        const res = await axios.get(`/api/courses?per_page=500`, {
            headers: { 'Authorization': `Bearer ${user.token}`, 'X-Editor': 'nanoms' }
        });
        return res.data.items;
    }, [user.token]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [categoriesData, coursesData] = await Promise.all([
                getCategories(),
                getCourses()
            ]);

            setAllCourses(coursesData);

            const popularCat = categoriesData.find(c => c.name === 'Most Popular');
            setMostPopularCategory(popularCat);

            if (popularCat) {
                const popularCourses = coursesData.filter(course => {
                    return getCategoryIds(course).includes(popularCat.id);
                });

                const sorted = popularCourses.sort((a, b) => {
                    const orderA = a.popular_sort_order ?? Number.MAX_SAFE_INTEGER;
                    const orderB = b.popular_sort_order ?? Number.MAX_SAFE_INTEGER;
                    if (orderA !== orderB) return orderA - orderB;
                    return a.id - b.id;
                });

                setCourses(sorted);
                snapshotRef.current = sorted;
            } else {
                setCourses([]);
                snapshotRef.current = [];
            }

            setIsDirty(false);
            setAddedIds([]);
            setRemovedIds([]);
            setPublishError(null);
        } catch (error) {
            console.error('Failed to load data:', error);
            alert('Failed to load courses');
        } finally {
            setLoading(false);
        }
    }, [getCategories, getCourses]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const onRowReorder = (e) => {
        setCourses(e.value);
        setIsDirty(true);
    };

    const addCourseToPopular = (courseId) => {
        const courseToAdd = allCourses.find(c => c.id === courseId);
        if (!courseToAdd) return;

        const alreadyInList = courses.some(c => c.id === courseId);
        if (alreadyInList) {
            alert('Course is already in the list');
            return;
        }

        setCourses(prev => [...prev, courseToAdd]);
        setAddedIds(prev => [...prev, courseId]);
        // If it was previously removed in this session, un-remove it
        setRemovedIds(prev => prev.filter(id => id !== courseId));
        setIsDirty(true);
        setShowAddDialog(false);
        setSearchTerm('');
    };

    const removeCourse = (courseId) => {
        const course = courses.find(c => c.id === courseId);
        if (!window.confirm(`Remove "${course?.name}" from Popular?`)) return;
        setCourses(prev => prev.filter(c => c.id !== courseId));
        setRemovedIds(prev => [...prev, courseId]);
        // If it was added in this session, un-add it
        setAddedIds(prev => prev.filter(id => id !== courseId));
        setIsDirty(true);
    };

    const discardChanges = () => {
        setCourses(snapshotRef.current);
        setIsDirty(false);
        setAddedIds([]);
        setRemovedIds([]);
        setPublishError(null);
    };

    const publish = async () => {
        if (!mostPopularCategory) return;

        setPublishing(true);
        setPublishError(null);
        try {
            const promises = [];

            // Update order for all current courses
            courses.forEach((course, idx) => {
                const categoryIds = getCategoryIds(course);
                const hasCategory = categoryIds.includes(mostPopularCategory.id);
                const payload = { popular_sort_order: idx };

                // If newly added, also add the category
                if (addedIds.includes(course.id) && !hasCategory) {
                    payload.categories = [...categoryIds, mostPopularCategory.id];
                }

                promises.push(
                    axios.put(`/api/courses/${course.id}`, payload, { headers })
                );
            });

            // Remove category from removed courses
            for (const id of removedIds) {
                const course = allCourses.find(c => c.id === id);
                if (!course) continue;
                const categoryIds = getCategoryIds(course).filter(
                    catId => catId !== mostPopularCategory.id
                );
                promises.push(
                    axios.put(`/api/courses/${id}`, {
                        categories: categoryIds,
                        popular_sort_order: null
                    }, { headers })
                );
            }

            await Promise.all(promises);

            // Reload to get fresh data and reset state
            await loadData();
        } catch (error) {
            console.error('Failed to publish:', error);
            setPublishError('Failed to publish changes. Please try again.');
        } finally {
            setPublishing(false);
        }
    };

    const nameBodyTemplate = (rowData) => {
        return <a href={`/editor?course=${rowData.id}`} target="_blank" rel="noopener noreferrer">{rowData.name}</a>;
    };

    const positionBodyTemplate = (_rowData, { rowIndex }) => {
        return rowIndex + 1;
    };

    const rowClassName = (rowData) => {
        if (addedIds.includes(rowData.id)) return { 'row-added': true };
        if (!isDirty) return {};
        const snapshotIndex = snapshotRef.current.findIndex(c => c.id === rowData.id);
        const currentIndex = courses.findIndex(c => c.id === rowData.id);
        if (snapshotIndex === -1) return {};
        if (snapshotIndex !== currentIndex) return { 'row-moved': true };
        return {};
    };

    const removeBodyTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-trash"
                className="p-button-sm p-button-outlined p-button-danger"
                onClick={() => removeCourse(rowData.id)}
                tooltip="Remove from Popular"
                tooltipOptions={{ position: 'top' }}
            />
        );
    };

    if (loading) {
        return (
            <div className="App">
                <Header />
                <div className="container-fluid mt-4">
                    <div className="text-center">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!mostPopularCategory) {
        return (
            <div className="App">
                <Header />
                <div className="container-fluid mt-4">
                    <div className="alert alert-warning">
                        <strong>Warning:</strong> "Most Popular" category not found. Please create it first in Manage Categories.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <style>{`
                .row-added > td { background-color: rgba(25, 135, 84, 0.15) !important; }
                .row-moved > td { background-color: rgba(255, 193, 7, 0.15) !important; }
            `}</style>
            <Header />
            <div className="container-fluid">
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <h2 className="mb-3">Manage Popular Order</h2>
                    </div>
                </div>

                <div className="row mt-2">
                    <div className="col-12 col-xl-10 mx-auto">
                        <div className="alert alert-info py-2 small mb-3" role="alert">
                            <strong>About Popular Order:</strong>
                            <ul className="mb-0">
                                <li>This page manages courses in the "Most Popular" category shown as "Popular on Nerdish"</li>
                                <li>Aim for 10-15 items (displayed as small vertical cards)</li>
                                <li>Drag rows to reorder courses</li>
                                <li>Click "Publish" to save all changes at once</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-12 col-xl-10 mx-auto">
                        {isDirty && (
                            <div className="alert alert-warning py-2 mb-3 d-flex align-items-center gap-2">
                                <i className="pi pi-exclamation-triangle"></i>
                                <span>You have unpublished changes</span>
                            </div>
                        )}

                        {publishError && (
                            <div className="alert alert-danger py-2 mb-3">
                                {publishError}
                            </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <strong>Category:</strong> {mostPopularCategory.name} (ID: {mostPopularCategory.id})
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    label="Add Course"
                                    icon="pi pi-plus"
                                    className="p-button-outlined"
                                    onClick={() => setShowAddDialog(true)}
                                    disabled={publishing}
                                />
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    className="p-button-outlined"
                                    onClick={loadData}
                                    disabled={publishing || isDirty}
                                    tooltip={isDirty ? 'Discard or publish changes first' : undefined}
                                />
                                <Button
                                    label="Discard Changes"
                                    icon="pi pi-undo"
                                    className="p-button-outlined p-button-secondary"
                                    onClick={discardChanges}
                                    disabled={!isDirty || publishing}
                                />
                                <div style={{ width: '3rem' }} />
                                <Button
                                    label="Publish"
                                    icon={publishing ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                    className="p-button-info"
                                    onClick={publish}
                                    disabled={!isDirty || publishing}
                                />
                            </div>
                        </div>

                        {courses.length === 0 ? (
                            <div className="alert alert-warning">
                                No courses in "Most Popular". Click "Add Course" to get started.
                            </div>
                        ) : (
                            <DataTable
                                value={courses}
                                responsiveLayout="scroll"
                                showGridlines
                                className="border"
                                reorderableRows
                                onRowReorder={onRowReorder}
                                rowClassName={rowClassName}
                            >
                                <Column rowReorder style={{ width: '3rem' }} />
                                <Column
                                    header="#"
                                    body={positionBodyTemplate}
                                    style={{ width: '60px' }}
                                />
                                <Column
                                    field="id"
                                    header="ID"
                                    style={{ width: '80px' }}
                                />
                                <Column
                                    field="name"
                                    header="Course Name"
                                    body={nameBodyTemplate}
                                    style={{ minWidth: '300px' }}
                                />
                                <Column
                                    field="status"
                                    header="Status"
                                    style={{ width: '120px' }}
                                />
                                <Column
                                    header=""
                                    body={removeBodyTemplate}
                                    style={{ width: '60px' }}
                                />
                            </DataTable>
                        )}
                    </div>
                </div>

                <Dialog
                    header="Add Course to Most Popular"
                    visible={showAddDialog}
                    style={{ width: '700px' }}
                    modal
                    onHide={() => {
                        setShowAddDialog(false);
                        setSearchTerm('');
                    }}
                >
                    <div className="mb-3">
                        <label className="form-label">Search Courses</label>
                        <InputText
                            type="text"
                            className="form-control"
                            placeholder="Search by name or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {(() => {
                            const currentIds = new Set(courses.map(c => c.id));
                            const availableCourses = allCourses.filter(course => {
                                return !currentIds.has(course.id);
                            });

                            const filtered = availableCourses.filter(course => {
                                if (!searchTerm) return true;
                                const search = searchTerm.toLowerCase();
                                return String(course.id).includes(search) ||
                                       (course.name || '').toLowerCase().includes(search);
                            });

                            if (filtered.length === 0) {
                                return (
                                    <div className="text-center text-muted py-4">
                                        {searchTerm ? 'No courses found matching search' : 'All courses are already in the list'}
                                    </div>
                                );
                            }

                            return (
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="sticky-top bg-light">
                                        <tr>
                                            <th style={{ width: '10%' }}>ID</th>
                                            <th style={{ width: '60%' }}>Name</th>
                                            <th style={{ width: '15%' }}>Status</th>
                                            <th style={{ width: '15%' }} className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(course => (
                                            <tr key={course.id}>
                                                <td>{course.id}</td>
                                                <td className="text-truncate" style={{ maxWidth: 400 }}>{course.name}</td>
                                                <td>{course.status}</td>
                                                <td className="text-end">
                                                    <Button
                                                        label="Add"
                                                        icon="pi pi-plus"
                                                        className="p-button-sm p-button-success"
                                                        onClick={() => addCourseToPopular(course.id)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            className="p-button-outlined"
                            onClick={() => {
                                setShowAddDialog(false);
                                setSearchTerm('');
                            }}
                        />
                    </div>
                </Dialog>
            </div>
        </div>
    );
};
